import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

/**
 * App modal. Rebuilt on Headless UI Dialog so it renders a proper centered,
 * scroll-locked overlay regardless of the legacy SCSS bundle (which is no
 * longer loaded). Keeps the original prop contract so every existing consumer
 * (InfoEventModal, RatingModal, ModalEditCancel, EditModal, CancelModal, gift)
 * works unchanged.
 *
 * Props: { shown, close, size, title, subtitle, footer, children, modalStyle }
 *   size: "modal--small" | "modal--medium" | "modal--large"
 */
const SIZE_MAX = {
  "modal--small": "max-w-md",
  "modal--medium": "max-w-xl",
  "modal--large": "max-w-3xl",
};

const Modal = ({ children, shown, size, close, modalStyle = "", title, subtitle, footer }) => {
  const maxW = SIZE_MAX[size] || SIZE_MAX["modal--medium"];

  return (
    <Transition show={!!shown} as={Fragment}>
      <Dialog onClose={close} className="relative z-[999]">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel
                className={`w-full ${maxW} rounded-2xl bg-white shadow-elevated ${modalStyle}`}
              >
                {/* Head */}
                <div className="relative border-b border-slate-100 px-5 py-4 text-center sm:px-6">
                  {title ? (
                    <Dialog.Title className="font-display text-lg font-semibold text-ink">
                      {title}
                    </Dialog.Title>
                  ) : null}
                  {subtitle ? <p className="mt-0.5 text-sm text-ink-muted">{subtitle}</p> : null}
                  <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink rtl:right-auto rtl:left-3"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="max-h-[70vh] overflow-y-auto px-5 py-5 sm:px-6">{children}</div>

                {/* Footer */}
                {footer ? (
                  <div className="border-t border-slate-100 px-5 py-4 sm:px-6">{footer}</div>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal;
